import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { errorMessage, errorRef } from '../api/client';
import { fetchDeliveryCode, requestCallBridge } from '../api/tracking';
import { showToast } from './useToast';

/** "now" that ticks every `everyMs` while `active` (ETA countdowns, late checks). */
export function useNow(everyMs: number, active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs, active]);
  return now;
}

/**
 * The delivery code card (W14 §5): fetched only when the order needs one and is still on its
 * way. Never cached to disk; react-query keeps it in memory for this session only.
 */
export function useDeliveryCode(orderId: string, needed: boolean) {
  const query = useQuery({
    queryKey: ['order-delivery-code', orderId],
    queryFn: () => fetchDeliveryCode(orderId),
    enabled: needed && orderId !== '',
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
  });
  return {
    loading: query.isLoading && needed,
    failed: query.isError,
    retry: () => void query.refetch(),
    view: query.data ?? null,
  };
}

/**
 * Masked call to the rider: ask the server for a bridge number, then hand it to the phone's
 * dialer. The real numbers are never seen by either side. When there is no number the
 * server's message (e.g. "use chat or call support") is shown instead.
 */
export function useRiderCall(orderId: string, messages: { unavailable: string }) {
  const call = useMutation({
    mutationFn: () => requestCallBridge(orderId),
    onSuccess: (result) => {
      if (result.dialNumber) {
        window.location.href = `tel:${result.dialNumber.replace(/[^\d+]/g, '')}`;
      } else {
        showToast(result.message ?? messages.unavailable);
      }
    },
    onError: (error) =>
      showToast(errorMessage(error, messages.unavailable), {
        tone: 'negative',
        ref: errorRef(error),
      }),
  });
  return { calling: call.isPending, call: () => call.mutate() };
}
